import React from 'react';

import { cva } from 'class-variance-authority';

import { cn } from './utils';


/**
 * Persistent in-app banner per the .pen "IN-APP BANNERS · PERSISTENT" pattern:
 * radius 10, tinted bg (~10%) + stroke (~33%), 30px icon box (radius 7, ~13% tint,
 * 16px accent icon), 13px ink text, right-aligned accent action.
 */
const bannerVariants = cva(
  'flex w-full items-center gap-[11px] rounded-[10px] border px-3.5 py-3',
  {
    variants: {
      variant: {
        danger: 'bg-danger/10 border-danger/35',
        primary: 'bg-primary/10 border-primary/35',
        warning: 'bg-warning/10 border-warning/35',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

// Per-variant tints for the icon box and the right-aligned action (kept as a
// small lookup; CVA drives the frame).
const TINTS = {
  danger: { iconBox: 'bg-danger/15 text-danger', action: 'text-danger' },
  primary: { iconBox: 'bg-primary/15 text-primary', action: 'text-primary' },
  warning: { iconBox: 'bg-warning/15 text-warning', action: 'text-warning' },
};

const Banner = React.forwardRef((props, ref) => {
  const {
    variant = 'primary',
    icon = null,
    action = null,
    className,
    children,
    ...restProps
  } = props;

  const tints = TINTS[variant] || TINTS.primary;

  return (
    <div
      ref={ref}
      className={cn(bannerVariants({ variant }), className)}
      role={variant === 'danger' ? 'alert' : 'status'}
      {...restProps}
    >
      {icon
        ? (
          <span
            className={cn(
              'grid size-[30px] shrink-0 place-items-center rounded-[7px] [&_svg]:size-4',
              tints.iconBox,
            )}
          >
            {icon}
          </span>
        )
        : null}

      <span className="flex-1 text-[13px] font-medium text-ink">{children}</span>

      {action
        ? (
          <span className={cn('shrink-0 text-[12.5px] font-semibold', tints.action)}>
            {action}
          </span>
        )
        : null}
    </div>
  );
});

Banner.displayName = 'Banner';

export { Banner, bannerVariants };
export default Banner;
